<script lang="ts">
export default {
  name: 'FileUploadModal',
};
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { File, FileSpreadsheet, CircleX, Check, TriangleAlert } from '@lucide/vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Spinner from '@/components/v2/Spinner.vue';
import { acceptContentTowerTypes } from '@/utilities/tower/Upload';

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle?: string;
    confirmText?: string;
    templateIcon?: string;
    templateName?: string;
    templatePath: string;
    uploadAction: (file: File | undefined) => Promise<string[] | undefined>;
    validate?: (file: File | undefined) => string[] | undefined;
  }>(),
  {
    subtitle: 'Use the template below to upload.',
    confirmText: 'Upload',
    templateIcon: undefined,
    templateName: 'Template',
    validate: undefined,
  },
);

const isUploading = ref(false);
const binaryData = ref('');
const filename = ref('');
const isValidating = ref(false);
const file = ref<File | undefined>(undefined);
const fileInput = ref<HTMLInputElement | null>(null);
const validationErrors = ref([] as string[]);
const uploadErrors = ref([] as string[]);

async function confirmAction(): Promise<boolean> {
  isUploading.value = true;
  uploadErrors.value = (await props.uploadAction(file.value)) || [];
  isUploading.value = false;

  if (uploadErrors.value.length > 0) {
    return false;
  }

  resetFile();

  return true;
}

function resetFile() {
  if (!isValidating.value && !isUploading.value) {
    binaryData.value = '';
    file.value = undefined;
    filename.value = '';
    validationErrors.value = [];
    uploadErrors.value = [];
    isValidating.value = false;
  }
}

function selectFile() {
  fileInput.value?.click();
}

function readFile(event: Event) {
  if (!event || !event.target) {
    return;
  }

  const files = (event.target as HTMLInputElement).files;
  if (!files || files.length === 0) {
    return;
  }

  isValidating.value = true;

  const reader = new FileReader();
  reader.onload = () => {
    try {
      binaryData.value = reader.result as string;
      filename.value = files[0].name;
      file.value = files[0];

      setTimeout(() => {
        validationErrors.value = props.validate ? props.validate(file.value) || [] : [];
        isValidating.value = false;
      }, 200);
    } catch (err) {
      console.error(err);
    } finally {
      if (fileInput.value) {
        fileInput.value.value = '';
      }
    }
  };
  reader.readAsArrayBuffer(files[0]);
}

const isFileValid = computed(() => {
  return !isValidating.value && file.value && validationErrors.value.length === 0;
});
</script>

<template>
  <div>
    <div style="float: right">
      <ConfirmModal
        :title="title"
        size="md"
        :action="confirmAction"
        :dismiss="resetFile"
        :confirmText="confirmText"
        :isConfirmDisabled="() => !isFileValid"
        :keep-open="isUploading || uploadErrors.length > 0"
        :submitLoading="isUploading">
        <template v-slot:button="{ props }">
          <slot
            name="buttonActivator"
            :props="props" />
        </template>
        <div>
          {{ subtitle }}

          <div class="mt-2">
            <a
              :href="templatePath"
              class="flex items-center gap-1">
              <FileSpreadsheet
                v-if="templateIcon"
                :size="18" />
              {{ templateName }}
            </a>
          </div>

          <hr class="mt-4 mb-4" />

          <AppButton
            v-if="!binaryData"
            data-test="select-file"
            autofocus
            variant="outline"
            size="sm"
            @click="selectFile">
            Select File
          </AppButton>

          <div
            v-else
            class="flex items-center">
            <File
              :size="18"
              class="mr-1" />
            <span>{{ filename }}</span>

            <button
              v-if="!isValidating"
              @click="resetFile"
              class="mx-2">
              <CircleX :size="18" />
            </button>

            <Spinner
              size="sm"
              class="ml-1"
              v-if="isValidating" />

            <template v-else>
              <Check
                v-if="validationErrors.length === 0"
                :size="18"
                class="text-green-600" />
              <span
                v-else
                class="text-error">
                <TriangleAlert
                  :size="18"
                  class="" />
              </span>
            </template>
          </div>
          <input
            ref="fileInput"
            type="file"
            @change="readFile"
            :accept="acceptContentTowerTypes"
            style="display: none" />

          <div
            class="property-validation-errors"
            v-if="validationErrors.length > 0 && !isValidating">
            <span class="text-error">Cannot upload due to the following:</span>
            <ul>
              <li
                v-for="(error, index) of validationErrors"
                :key="index">
                {{ error }}
              </li>
            </ul>
          </div>

          <div
            class="property-upload-errors"
            v-if="uploadErrors.length > 0">
            <span class="text-error">Upload failed due to the following:</span>
            <ul>
              <li
                v-for="(error, index) of uploadErrors"
                :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
        </div>
      </ConfirmModal>
    </div>
  </div>
</template>

<style scoped>
hr {
  border: 0;
  height: 1px;
  background: #cccccc;
}
</style>
